import { Component, Injector, Input, OnInit, computed, inject, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { BaseModalComponent } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import {
  AdvanceRequestCandidate,
  CashRequest,
  CashRequestCandidate,
  CashRequestDocumentsUpdate,
  Currency,
  EntityKeyByReference,
  PaymentRequestCandidate,
  WithholdingCandidate,
} from '../../../../models/cash-request/cash-request';
import { ApPaymentRequestStatus } from '../../../../models/ap-payment-request/ap-payment-request';
import { ApDocumentStatus } from '../../../../models/ap-document/ap-document';
import { CashRequestProxyService } from '../../../../proxies/cash-request.proxy.service';
import { EnumService } from '../../../../services/enum.service';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { ApDocumentQuickViewModalComponent } from '../../../shared/modals/ap-document-quick-view-modal/ap-document-quick-view-modal.component';
import { ModalService } from '@card-software/library';

/** Identity of a candidate across both tabs — a reference alone is not unique between entity types. */
function candidateKey(candidate: CashRequestCandidate): string {
  return `${candidate.sourceDocument.entityDefinitionCode}:${candidate.sourceDocument.reference}`;
}

/** The AP documents behind a row, in one shape so both tabs render from the same template: a payment
 *  request lists the invoices it pays, a withholding points at the single invoice it was taken from. */
export interface CandidateDocumentRow {
  reference?: string | null;
  number?: string | null;
  fiscalNumber?: string | null;
  /** What the request pays on this document. Null for a withholding, which pays the tax, not the invoice. */
  amount: number | null;
  currency: Currency | null;
}

/** One provider's rows on one tab — rendered as a card with the provider as its header. */
export interface CandidateGroup {
  reference: string;
  name: string;
  code: string;
  rows: CashRequestCandidate[];
}

type TagSeverity = 'success' | 'secondary' | 'info' | 'warn' | 'danger';

/**
 * The Cash Request document picker (#4864) — plan payment requests and withholdings against a draft.
 *
 * <p>Both tabs come from ONE call: candidates carry their provider, their GL split and their planning state
 * already resolved, so filtering and totalling happen on what is already in hand and no keystroke costs a
 * round trip. Only the "include already planned" toggle re-queries, because the server excludes those rows
 * entirely.</p>
 *
 * <p>Rows already on this request come back CHECKED rather than disabled: the endpoint takes a delta, so
 * unchecking one is how you unplan it, and planning and unplanning stay in the same place. What is committed
 * is only what changed — never the full selection, which would silently unplan whatever the active filter
 * happened to hide.</p>
 *
 * <p>Selection is a Set of keys rather than PrimeNG's `[(selection)]` arrays: it spans two tables, has to
 * survive a re-query, and drives a live total in the footer.</p>
 */
@Component({
  selector: 'app-cash-request-document-picker-modal',
  templateUrl: './cash-request-document-picker-modal.component.html',
})
export class CashRequestDocumentPickerModalComponent extends BaseModalComponent<CashRequest | null> implements OnInit {
  private readonly service = inject(CashRequestProxyService);
  private readonly enumService = inject(EnumService);
  private readonly translate = inject(TranslateService);
  private readonly modalService = inject(ModalService);

  /** The draft being planned. */
  @Input({ required: true }) cashRequestId!: string;

  readonly isLoading = signal(0);
  readonly paymentRequests = signal<PaymentRequestCandidate[]>([]);
  readonly withholdings = signal<WithholdingCandidate[]>([]);
  /** Approved vendor advances (#6323). The server already leaves out any planned into another request. */
  readonly advanceRequests = signal<AdvanceRequestCandidate[]>([]);

  /** Everything currently ticked, by candidate key. Preloaded with what is already on the request. */
  private readonly selectedKeys = signal<ReadonlySet<string>>(new Set());
  /** What was already planned when the picker opened — the baseline the delta is measured against. */
  private plannedOnOpen: ReadonlySet<string> = new Set();

  /** `string` rather than a union because p-tabs models its value as `string | number`. */
  readonly activeTab = signal<string>('requests');

  readonly searchControl = new FormControl<string>('', { nonNullable: true });
  private readonly search = toSignal(this.searchControl.valueChanges, { initialValue: '' });

  readonly providerControl = new FormControl<string | null>(null);
  private readonly provider = toSignal(this.providerControl.valueChanges, { initialValue: null });

  readonly glAccountControl = new FormControl<string | null>(null);
  private readonly glAccount = toSignal(this.glAccountControl.valueChanges, { initialValue: null });

  /** Server-side: rows planned into another request are excluded unless this is on. */
  readonly includePlannedControl = new FormControl<boolean>(false, { nonNullable: true });

  private currencyLabels: Record<number, string> = {};
  private paymentRequestStatusLabels: Record<number, string> = {};
  private withholdingStatusLabels: Record<number, string> = {};

  constructor(injector: Injector) {
    super(injector);
  }

  ngOnInit(): void {
    this.currencyLabels = this.enumService.getEnum('Currency').values as Record<number, string>;
    this.paymentRequestStatusLabels = this.enumService.getEnum('ApPaymentRequestStatus').values as Record<number, string>;
    this.withholdingStatusLabels = this.enumService.getEnum('ApDocumentStatus').values as Record<number, string>;
    this.load();
  }

  private t(key: string, fallback: string): string {
    const value = this.translate.instant(key);
    return value === key ? fallback : value;
  }

  // ── Loading ─────────────────────────────────────────────────────────────────

  /**
   * Reloads both tabs. The selection is re-derived from what the server says is planned, so a re-query can
   * never leave a tick pointing at a row that is no longer offered.
   */
  load(): void {
    if (!this.cashRequestId) return;
    this.isLoading.update((v) => v + 1);
    this.service
      .getCandidates(this.cashRequestId, { includePlanned: this.includePlannedControl.value })
      .pipe(finalize(() => this.isLoading.update((v) => v - 1)))
      .subscribe({
        next: (response) => {
          const data = response.data;
          this.paymentRequests.set(data?.paymentRequests ?? []);
          this.withholdings.set(data?.withholdings ?? []);
          this.advanceRequests.set(data?.advanceRequests ?? []);

          const planned = new Set<string>(
            this.allCandidates()
              .filter((candidate) => candidate.alreadyInThisRequest)
              .map(candidateKey),
          );
          this.plannedOnOpen = planned;
          this.selectedKeys.set(new Set(planned));
        },
        error: () => {
          this.paymentRequests.set([]);
          this.withholdings.set([]);
          this.advanceRequests.set([]);
        },
      });
  }

  private allCandidates(): CashRequestCandidate[] {
    return [...this.paymentRequests(), ...this.withholdings(), ...this.advanceRequests()];
  }

  // ── Filtering (client-side — everything is already in hand) ──────────────────

  readonly providerOptions = computed(() => {
    const byReference = new Map<string, string>();
    for (const candidate of this.allCandidates()) {
      const reference = candidate.provider?.reference;
      if (!reference || byReference.has(reference)) continue;
      const code = candidate.provider?.code;
      const name = candidate.provider?.name ?? '';
      byReference.set(reference, code ? `${code} — ${name}` : name);
    }
    return Array.from(byReference.entries())
      .map(([value, label]) => ({ label, value }))
      .sort((a, b) => a.label.localeCompare(b.label));
  });

  /** Every GL account the loaded candidates would land on — the filter's options, with no extra call. */
  readonly glAccountOptions = computed(() => {
    const byReference = new Map<string, string>();
    for (const candidate of this.allCandidates()) {
      for (const account of candidate.accounts) {
        if (!account.reference || byReference.has(account.reference)) continue;
        byReference.set(account.reference, [account.number, this.accountName(account)].filter((v) => !!v).join(' · '));
      }
    }
    return Array.from(byReference.entries())
      .map(([value, label]) => ({ label, value }))
      .sort((a, b) => a.label.localeCompare(b.label));
  });

  readonly filteredPaymentRequests = computed(() => {
    const term = this.search().trim().toLowerCase();
    return this.byProvider(
      this.paymentRequests().filter(
        (candidate) =>
          this.matchesFilters(candidate) &&
          (!term ||
            this.matchesShared(candidate, term) ||
            candidate.documents.some(
              (document) =>
                (document.sourceDocument.number ?? '').toLowerCase().includes(term) ||
                (document.fiscalNumber ?? '').toLowerCase().includes(term),
            )),
      ),
    );
  });

  readonly filteredWithholdings = computed(() => {
    const term = this.search().trim().toLowerCase();
    return this.byProvider(
      this.withholdings().filter(
        (candidate) =>
          this.matchesFilters(candidate) &&
          (!term || this.matchesShared(candidate, term) || (candidate.certificateNumber ?? '').toLowerCase().includes(term)),
      ),
    );
  });

  readonly filteredAdvanceRequests = computed(() => {
    const term = this.search().trim().toLowerCase();
    return this.byProvider(
      this.advanceRequests().filter(
        (candidate) =>
          this.matchesFilters(candidate) &&
          (!term ||
            this.matchesShared(candidate, term) ||
            candidate.purchaseOrders.some((po) => (po.number ?? '').toLowerCase().includes(term))),
      ),
    );
  });

  /**
   * Sorts by provider, then by date. Not cosmetic: p-table's subheader grouping only detects a group
   * boundary between CONSECUTIVE rows, so an unsorted list would repeat the same provider header.
   */
  private byProvider<T extends CashRequestCandidate>(candidates: T[]): T[] {
    return [...candidates].sort(
      (a, b) =>
        (a.provider?.name ?? '').localeCompare(b.provider?.name ?? '') ||
        (a.provider?.reference ?? '').localeCompare(b.provider?.reference ?? '') ||
        (a.documentDate ?? '').localeCompare(b.documentDate ?? ''),
    );
  }

  private matchesFilters(candidate: CashRequestCandidate): boolean {
    const provider = this.provider();
    if (provider && candidate.provider?.reference !== provider) return false;
    const account = this.glAccount();
    if (account && !candidate.accounts.some((a) => a.reference === account)) return false;
    return true;
  }

  private matchesShared(candidate: CashRequestCandidate, term: string): boolean {
    return (
      (candidate.sourceDocument.number ?? '').toLowerCase().includes(term) ||
      (candidate.provider?.name ?? '').toLowerCase().includes(term) ||
      (candidate.provider?.code ?? '').toLowerCase().includes(term)
    );
  }

  clearFilters(): void {
    this.searchControl.setValue('');
    this.providerControl.setValue(null);
    this.glAccountControl.setValue(null);
  }

  readonly hasFilters = computed(() => !!this.search().trim() || !!this.provider() || !!this.glAccount());

  // ── Provider grouping ───────────────────────────────────────────────────────

  readonly paymentRequestGroups = computed(() => this.toGroups(this.filteredPaymentRequests()));
  readonly withholdingGroups = computed(() => this.toGroups(this.filteredWithholdings()));
  readonly advanceRequestGroups = computed(() => this.toGroups(this.filteredAdvanceRequests()));

  /**
   * Splits a tab's rows into one group per provider. Each group renders as its own card, so the provider
   * reads as the heading of its rows rather than as one more row in a long table. Relies on `byProvider`
   * having already put a provider's rows next to each other.
   */
  private toGroups<T extends CashRequestCandidate>(rows: T[]): CandidateGroup[] {
    const groups: CandidateGroup[] = [];
    for (const row of rows) {
      const reference = row.provider?.reference ?? '';
      const last = groups[groups.length - 1];
      if (last && last.reference === reference) {
        last.rows.push(row);
      } else {
        groups.push({ reference, name: row.provider?.name ?? '', code: row.provider?.code ?? '', rows: [row] });
      }
    }
    return groups;
  }

  /**
   * What planning the rest of this provider would still add, in USD — the rows NOT already on the request.
   * A provider whose every document is already planned reads 0, which is the useful answer.
   */
  groupTotal(group: CandidateGroup): number {
    return group.rows.filter((r) => !r.alreadyInThisRequest).reduce((sum, r) => sum + (r.functionalAmount ?? 0), 0);
  }

  groupSelected(group: CandidateGroup): boolean {
    return this.allVisibleSelected(group.rows);
  }

  toggleGroup(group: CandidateGroup): void {
    this.toggleAllVisible(group.rows);
  }

  // ── Expansion ───────────────────────────────────────────────────────────────

  /**
   * Rows showing their full AP-document (or purchase-order) list, keyed by `dataKey` the way p-table's
   * `expandedRowKeys` wants it. A row only shows its first document until opened, so a request paying ten
   * invoices stays the same height as one paying a single invoice.
   */
  readonly expandedRows = signal<Record<string, boolean>>({});

  isExpanded(candidate: CashRequestCandidate): boolean {
    return !!this.expandedRows()[candidate.sourceDocument.reference ?? ''];
  }

  toggleExpanded(candidate: CashRequestCandidate): void {
    const key = candidate.sourceDocument.reference ?? '';
    const next = { ...this.expandedRows() };
    if (next[key]) delete next[key];
    else next[key] = true;
    this.expandedRows.set(next);
  }

  // ── Row content ─────────────────────────────────────────────────────────────

  /** The purchase orders an advance-request row is raised against (#6323). Empty for the other tabs. */
  purchaseOrdersOf(candidate: CashRequestCandidate): AdvanceRequestCandidate['purchaseOrders'] {
    return (candidate as AdvanceRequestCandidate).purchaseOrders ?? [];
  }

  /**
   * One array per row, built once. The expanded panel binds it to a nested p-table's `[value]`; a fresh array
   * on every change-detection pass makes that table re-render forever and freezes the page.
   */
  private readonly documentsCache = new WeakMap<CashRequestCandidate, CandidateDocumentRow[]>();

  /** The AP documents behind a row, in one shape for both tabs. */
  documentsOf(candidate: CashRequestCandidate): CandidateDocumentRow[] {
    let documents = this.documentsCache.get(candidate);
    if (!documents) {
      documents = this.buildDocuments(candidate);
      this.documentsCache.set(candidate, documents);
    }
    return documents;
  }

  private buildDocuments(candidate: CashRequestCandidate): CandidateDocumentRow[] {
    const asPaymentRequest = candidate as PaymentRequestCandidate;
    if (asPaymentRequest.documents) {
      return asPaymentRequest.documents.map((document) => ({
        reference: document.sourceDocument.reference,
        number: document.sourceDocument.number,
        fiscalNumber: document.fiscalNumber,
        amount: document.amount,
        currency: document.currency,
      }));
    }
    // A withholding was taken from exactly one invoice.
    const invoice = (candidate as WithholdingCandidate).invoiceReference;
    return invoice?.reference
      ? [{ reference: invoice.reference, number: invoice.number, fiscalNumber: null, amount: null, currency: null }]
      : [];
  }

  /**
   * Read-only view of one AP document, without leaving the picker. Nested modal: ModalService slots each
   * one at its own z-index, so the picker stays open underneath with the selection intact.
   */
  openDocumentQuickView(documentId: string | null | undefined): void {
    if (documentId) this.modalService.open(ApDocumentQuickViewModalComponent, { modalClass: 'modal_middle' }, { documentId }).subscribe();
  }

  /**
   * The vendor advance a row earmarks, already deducted from its amounts (#4349). Zero for a withholding,
   * which has no advance to draw on — the two tabs share one row template, so the question is asked of both.
   */
  advanceApplied(candidate: CashRequestCandidate): number {
    return (candidate as PaymentRequestCandidate).advanceAppliedAmount ?? 0;
  }

  /**
   * Covered in full by an advance: the row is worth planning at zero, because it settles without cash.
   *
   * <p>Measured against what the cell RENDERS rather than against an exact zero. The API's arithmetic is
   * exact today, but a row displaying 0.00 while the badge explaining it stayed hidden would read as a bug
   * in the figure, which is the one thing this badge exists to prevent.</p>
   */
  isFullyAdvanceSettled(candidate: CashRequestCandidate): boolean {
    return this.advanceApplied(candidate) > 0 && Math.abs(candidate.functionalAmount) < 0.005;
  }

  accountName(account: { name?: { es: string; en: string } | null; number?: string | null }): string {
    const lang = this.translate.currentLang || this.translate.getDefaultLang();
    const name = account.name as Record<string, string> | null | undefined;
    return name?.[lang] || name?.['en'] || '';
  }

  // ── Selection ───────────────────────────────────────────────────────────────

  isSelected(candidate: CashRequestCandidate): boolean {
    return this.selectedKeys().has(candidateKey(candidate));
  }

  toggle(candidate: CashRequestCandidate): void {
    const key = candidateKey(candidate);
    const next = new Set(this.selectedKeys());
    if (!next.delete(key)) next.add(key);
    this.selectedKeys.set(next);
  }

  /** True only when every row currently visible on this tab is ticked. */
  allVisibleSelected(rows: CashRequestCandidate[]): boolean {
    return rows.length > 0 && rows.every((row) => this.isSelected(row));
  }

  /** Header checkbox — acts on the FILTERED rows only, never on what the filter is hiding. */
  toggleAllVisible(rows: CashRequestCandidate[]): void {
    const selectAll = !this.allVisibleSelected(rows);
    const next = new Set(this.selectedKeys());
    for (const row of rows) {
      const key = candidateKey(row);
      if (selectAll) next.add(key);
      else next.delete(key);
    }
    this.selectedKeys.set(next);
  }

  readonly selectedCount = computed(() => this.selectedKeys().size);

  /** USD the current selection would put on the request — the number the planner is actually deciding on. */
  readonly selectedTotal = computed(() => {
    const keys = this.selectedKeys();
    return this.allCandidates()
      .filter((candidate) => keys.has(candidateKey(candidate)))
      .reduce((sum, candidate) => sum + (candidate.functionalAmount ?? 0), 0);
  });

  /** What Save would send: ticked-but-not-planned, and planned-but-now-unticked. */
  private buildDelta(): CashRequestDocumentsUpdate {
    const selected = this.selectedKeys();
    const toKey = (candidate: CashRequestCandidate): EntityKeyByReference => ({
      reference: candidate.sourceDocument.reference!,
      entityDefinitionCode: candidate.sourceDocument.entityDefinitionCode!,
    });

    const candidates = this.allCandidates();
    return {
      add: candidates.filter((c) => selected.has(candidateKey(c)) && !this.plannedOnOpen.has(candidateKey(c))).map(toKey),
      remove: candidates.filter((c) => !selected.has(candidateKey(c)) && this.plannedOnOpen.has(candidateKey(c))).map(toKey),
    };
  }

  readonly addCount = computed(() => {
    const selected = this.selectedKeys();
    return this.allCandidates().filter((c) => selected.has(candidateKey(c)) && !this.plannedOnOpen.has(candidateKey(c))).length;
  });

  readonly removeCount = computed(() => {
    const selected = this.selectedKeys();
    return this.allCandidates().filter((c) => !selected.has(candidateKey(c)) && this.plannedOnOpen.has(candidateKey(c))).length;
  });

  readonly hasChanges = computed(() => this.addCount() > 0 || this.removeCount() > 0);

  // ── Display helpers ─────────────────────────────────────────────────────────

  currencyLabel(currency: Currency | null | undefined): string {
    return currency === null || currency === undefined ? '' : (this.currencyLabels[currency] ?? '');
  }

  paymentRequestStatusLabel(status: number): string {
    return this.paymentRequestStatusLabels[status] ?? '';
  }

  withholdingStatusLabel(status: number): string {
    return this.withholdingStatusLabels[status] ?? '';
  }

  /** Colour of the status tag: green when it can be paid, amber when part-way, red when it is out. */
  paymentRequestStatusSeverity(status: ApPaymentRequestStatus): TagSeverity {
    switch (status) {
      case ApPaymentRequestStatus.Approved:
        return 'success';
      case ApPaymentRequestStatus.PendingApproval:
        return 'warn';
      case ApPaymentRequestStatus.Rejected:
      case ApPaymentRequestStatus.Cancelled:
        return 'danger';
      default:
        return 'secondary';
    }
  }

  withholdingStatusSeverity(status: ApDocumentStatus): TagSeverity {
    switch (status) {
      case ApDocumentStatus.Paid:
        return 'success';
      case ApDocumentStatus.PartiallyPaid:
        return 'warn';
      case ApDocumentStatus.Posted:
        return 'info';
      case ApDocumentStatus.Cancelled:
        return 'danger';
      default:
        return 'secondary';
    }
  }

  /** Invoice numbers behind a payment request — the tooltip on its document count. */
  documentSummary(candidate: PaymentRequestCandidate): string {
    return candidate.documents
      .map((document) => document.sourceDocument.number ?? document.fiscalNumber ?? '')
      .filter((value) => !!value)
      .join(', ');
  }

  /** Numbers of the other cash requests a row is planned into, for the "planned elsewhere" tag. */
  otherRequests(candidate: CashRequestCandidate): string {
    return candidate.cashRequestReferences
      .map((reference) => reference.number ?? '')
      .filter((value) => !!value)
      .join(', ');
  }

  // ── Commit ──────────────────────────────────────────────────────────────────

  onSave(): void {
    const delta = this.buildDelta();
    if (delta.add.length === 0 && delta.remove.length === 0) {
      this.close({ action: 'Cancel', data: null });
      return;
    }

    this.isLoading.update((v) => v + 1);
    this.service
      .updateDocuments(this.cashRequestId, delta)
      .pipe(finalize(() => this.isLoading.update((v) => v - 1)))
      .subscribe({
        next: (response) => {
          if (!response.success || !response.data) return;
          GlobalMessageUtil.success(
            this.t('CashRequest', 'Cash Request'),
            this.t('CashRequest_DocumentsPlanned', 'The request was updated with the selected documents.'),
          );
          this.close({ action: 'Save', data: response.data });
        },
        // The picker stays open on failure — the selection is the user's work and closing would discard it.
        error: () => undefined,
      });
  }

  onClose(): void {
    this.close({ action: 'Cancel', data: null });
  }
}
